package com.castly.admin.ui.icons

import androidx.compose.runtime.Composable
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.Element

private const val SVG_NS = "http://www.w3.org/2000/svg"

/**
 * Low-level SVG element builder. Creates an SVG-namespaced DOM element.
 * Use this for any SVG tag (svg, path, line, circle, polyline, rect, etc.).
 */
@Composable
fun SvgElement(
    tag: String,
    attrs: (AttrsScope<Element>.() -> Unit)? = null,
    content: (@Composable ElementScope<Element>.() -> Unit)? = null,
) {
    TagElement(
        elementBuilder = object : ElementBuilder<Element> {
            override fun create(): Element =
                document.createElementNS(SVG_NS, tag).unsafeCast<Element>()
        },
        applyAttrs = attrs,
        content = content,
    )
}

/**
 * Convenience wrapper for a complete SVG icon. Sets standard Feather-style attributes:
 * fill=none, stroke=color, stroke-width=2, stroke-linecap=round, stroke-linejoin=round.
 *
 * @param size  Icon width/height in pixels (default 20)
 * @param color Stroke color — defaults to "currentColor" so it inherits the parent's CSS color
 * @param viewBox SVG viewBox (default "0 0 24 24" — standard Feather icons)
 * @param content Child SVG elements (path, line, circle, etc.)
 */
@Composable
fun SvgIcon(
    size: Int = 20,
    color: String = "currentColor",
    viewBox: String = "0 0 24 24",
    content: @Composable ElementScope<Element>.() -> Unit,
) {
    SvgElement(
        tag = "svg",
        attrs = {
            attr("xmlns", SVG_NS)
            attr("width", "$size")
            attr("height", "$size")
            attr("viewBox", viewBox)
            attr("fill", "none")
            attr("stroke", color)
            attr("stroke-width", "2")
            attr("stroke-linecap", "round")
            attr("stroke-linejoin", "round")
        },
        content = content,
    )
}
